import { createFileRoute } from "@tanstack/react-router";
import {
  Container,
  DirectAnswer,
  CtaBand,
  Eyebrow,
  Lead,
  TextLink,
} from "@/components/site/primitives";
import { SERVICES, SITE, TESTIMONIALS } from "@/lib/site-content";
import { ogImageMeta } from "@/lib/og";
import { RelatedLinks } from "@/components/site/RelatedLinks";
import { Section } from "@/components/site/sections";
import { AREAS, jsonLdScript, serviceSchema } from "@/lib/schema";

const title = "Fractional CTO services for startups and SMEs | Scryla";
const description =
  "Missed deadlines or an underperforming tech team? Fractional and interim CTO support: technology audits, strategy, delivery and coaching for founders.";

export const Route = createFileRoute("/what-i-do")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE.url}/what-i-do` },
      ...ogImageMeta(
        "what-i-do",
        "What I do, missed deadlines, stalled delivery, a team not performing",
      ),
    ],
    links: [{ rel: "canonical", href: `${SITE.url}/what-i-do` }],
    scripts: [
      jsonLdScript(
        serviceSchema({
          path: "/what-i-do",
          name: "Fractional CTO and technology advisory | Timothy Ng, Scryla",
          serviceType: "Fractional CTO",
          areaServed: AREAS.global,
          offerNames: SERVICES.map((service) => service.title),
        }),
      ),
    ],
  }),
  component: WhatIDoPage,
});
const PAINS: { title: string; body: string }[] = [
  {
    title: "The roadmap and the delivery reality no longer match",
    body: "What the board was shown last quarter and what is actually shippable have drifted apart, and nobody inside the team wants to be the one to say so.",
  },
  {
    title: "An outsourced team nobody is holding to account",
    body: "Invoices arrive, progress is described rather than demonstrated, and there is no independent read on whether the code you are paying for is any good.",
  },
  {
    title: "A decision that is expensive to unwind",
    body: "Rebuild against refactor, build against buy, a platform choice or a supplier commitment. It needs someone with no incentive in the answer.",
  },
  {
    title: "Diligence or an enterprise deal is coming",
    body: "A security questionnaire, an investor technical review or an acquirer. You need to know what will be found before somebody else finds it.",
  },
  {
    title: "Cost is climbing and no one can explain it",
    body: "Cloud, licences, contractors and tooling, growing faster than revenue, with no single person accountable for the line.",
  },
  {
    title: "The board cannot see technology clearly",
    body: "Reporting is either too technical to act on or too vague to trust, so risk only surfaces when it has already become a problem.",
  },
];

const STEPS = [
  {
    n: "01",
    title: "A conversation",
    body: "You describe where you are and what is worrying you. I tell you plainly whether I am the right person, and what I would look at first.",
  },
  {
    n: "02",
    title: "Agree the right starting point",
    body: "Sometimes that is an audit and written report. Sometimes it is a board session, support alongside the CTO, a review of one decision or immediate involvement in a programme. I set out the smallest useful engagement first.",
  },

  {
    n: "03",
    title: "Ongoing involvement",
    body: "Board attendance, supplier management, hiring, architecture calls, or steering a specific programme. Whatever the business actually needs.",
  },
  {
    n: "04",
    title: "Handing over",
    body: "When you are ready to bring the role in-house, I help you hire well and step back.",
  },
];

const ALONGSIDE = [
  {
    title: "A project or programme",
    body: "Your CTO cannot pause the rest of the estate to run one large piece of work. I own that piece, report into them, and hand it back finished.",
  },
  {
    title: "A restructure or scale-up",
    body: "Reshaping a team, changing how delivery works, or bringing an outsourced function in-house. Extra senior capacity while the change is live, not a permanent second chair.",
  },
  {
    title: "A sounding board",
    body: "Somewhere to test a decision before it reaches the board or the investors. Architecture, build against buy, supplier choice, hiring. Said plainly, in confidence.",
  },
];

function WhatIDoPage() {
  return (
    <div>
      <section
        aria-labelledby="what-i-do-hero-heading"
        className="border-b border-border bg-gradient-to-b from-background to-surface"
      >
        <Container>
          <Eyebrow>Fractional CTO</Eyebrow>
          <h1
            id="what-i-do-hero-heading"
            className="mt-4 max-w-[22ch] text-[34px] leading-[1.06] font-bold tracking-[-0.035em] md:text-5xl"
          >
            Missed deadlines, stalled delivery, a tech team that is not performing.
          </h1>
          <DirectAnswer className="mt-6">
            I work with founders and boards as a fractional CTO and technology non-executive
            director: auditing where technology, team, spend and risk stand today, setting a plan
            the board can verify, holding in-house and outsourced teams to what they promised, and
            coaching technical leads so the capability stays in the business.
          </DirectAnswer>
          <Lead className="mt-5">
            I solve business challenges by improving your approach to technology. From steering
            projects to advising stakeholders to helping secure investment, the work is tailored to
            your needs and business goals.
          </Lead>
        </Container>
      </section>

      <Section
        heading="People usually come to me with one of these"
        headingId="pains-heading"
        tone="surface"
        divider="bottom"
      >
        <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {PAINS.map((pain) => (
            <li key={pain.title} className="rounded-lg border border-border bg-card p-6">
              <h3 className="text-[17px] leading-snug font-semibold text-ink">{pain.title}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-body">{pain.body}</p>
            </li>
          ))}
        </ul>
        <p className="mt-8 max-w-[62ch] text-base leading-relaxed text-body">
          If one of those is the reason you are reading this, the useful next step is a conversation
          rather than a proposal. You can also{" "}
          <TextLink to="/technology-decision-risk-check">
            run the technology decision risk check
          </TextLink>{" "}
          and get the read on the page in five minutes.
        </p>
      </Section>

      <Section heading="What you actually get" headingId="services-heading" divider="bottom">
        <ul className="grid gap-5 md:grid-cols-2">
          {SERVICES.map((service) => (
            <li key={service.title} className="rounded-lg border border-border bg-card p-6">
              <h3 className="text-lg font-semibold">{service.title}</h3>
              {service.title === "Support alongside your existing CTO" ? (
                <p className="mt-3 text-[15px] leading-relaxed text-body">
                  <a href="#alongside-cto-heading" className="underline underline-offset-4">
                    How this works alongside an in-post CTO
                  </a>
                </p>
              ) : (
                <p className="mt-3 text-[15px] leading-relaxed text-body">{service.long}</p>
              )}
            </li>
          ))}
        </ul>

        <RelatedLinks
          className="mt-8"
          label="Fractional CTO by region"
          paths={["/fractional-cto-uk", "/fractional-cto-us", "/fractional-cto-southeast-asia"]}
        />
        <p className="mt-4 text-[15px]">
          <TextLink to="/case-studies/financial-services-technology-transformation">
            Case study: technology as the competitive lever in financial services
          </TextLink>
        </p>
        <RelatedLinks
          className="mt-8"
          label="For investors"
          paths={["/technical-due-diligence-investors-uk"]}
        />
        <p className="mt-4 max-w-[62ch] text-[15px] leading-relaxed text-body">
          When the work belongs to a live deal rather than a portfolio review,{" "}
          <TextLink to="/technology-due-diligence-ma">
            Technology due diligence for M&amp;A
          </TextLink>{" "}
          covers buying, selling and preparing a business for buyer scrutiny.
        </p>
        <p className="mt-6 max-w-[62ch] text-[15px] leading-relaxed text-body">
          The arrangement is also described as a part-time CTO or a virtual CTO. The label matters
          less than the mandate: what the person decides, what they only recommend, and what the
          board is told. <TextLink to="/part-time-cto">Part-time CTO</TextLink> covers how the
          arrangement is sized and what it costs,{" "}
          <TextLink to="/how-to-hire-a-fractional-cto">How to hire a fractional CTO</TextLink> sets
          out the questions to ask before you appoint one, and{" "}
          <TextLink to="/fractional-cto-cost">What a fractional CTO costs</TextLink> covers the
          published rate benchmarks.
        </p>
      </Section>

      <Section
        eyebrow="AI in the operating model"
        heading="AI governance and assurance"
        headingId="ai-governance-heading"
        divider="bottom"
      >
        <p className="max-w-[62ch] text-base leading-relaxed text-body">
          Once AI is doing real work rather than answering the odd question, the hard part is not
          the model. It is deciding what it may do without approval, which source it should treat as
          authoritative, how it behaves when the evidence is thin, and what keeps one client or
          project out of the reasoning applied to another. I work through those decisions with you
          and put the controls outside the model, where they hold.
        </p>
        <RelatedLinks className="mt-7" paths={["/ai-governance-for-leadership-teams"]} />
      </Section>

      <Section
        eyebrow="If you already have a CTO"
        heading="Working alongside your CTO, not over the top of them"
        headingId="alongside-cto-heading"
        tone="surface"
        divider="bottom"
      >
        <p className="max-w-[62ch] text-base leading-relaxed text-body">
          A good chunk of my work is not replacing anybody. It is taking weight off a CTO who is
          already doing the job well and has more on than one person can reasonably carry. It is
          temporary, it runs to their agenda, and it stops when the pressure does.
        </p>
        <ul className="mt-8 grid gap-5 md:grid-cols-3">
          {ALONGSIDE.map((item) => (
            <li key={item.title} className="rounded-lg border border-border bg-card p-6">
              <h3 className="text-lg font-semibold">{item.title}</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-body">{item.body}</p>
            </li>
          ))}
        </ul>
        <figure className="mt-8 border-l-2 border-primary pl-5">
          <blockquote className="max-w-[62ch] text-[17px] leading-relaxed text-ink">
            &ldquo;{TESTIMONIALS.danielReilly.quote}&rdquo;
          </blockquote>
          <figcaption className="mt-3 text-sm text-muted-foreground">
            {TESTIMONIALS.danielReilly.name}, {TESTIMONIALS.danielReilly.role}
          </figcaption>
        </figure>
      </Section>

      <section aria-labelledby="how-i-work-heading" className="border-b border-border bg-surface">
        <Container>
          <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
            <div className="min-w-0">
              <Eyebrow>How I work</Eyebrow>
              <h2
                id="how-i-work-heading"
                className="mt-3 text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
              >
                CTO-level judgement, without a full-time CTO
              </h2>
              <p className="mt-4 max-w-[52ch] text-base leading-relaxed text-body">
                I take on a small number of clients at a time so that each one gets a level of
                personalisation larger consultancies cannot match. Access my expertise as often as
                you need.
              </p>
            </div>
            <ol className="grid gap-5 sm:grid-cols-2">
              {STEPS.map((step) => (
                <li
                  key={step.n}
                  className="grid grid-cols-[52px_1fr] gap-3 rounded-lg border border-border bg-card p-6"
                >
                  <span className="text-sm font-semibold text-primary">{step.n}</span>
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold">{step.title}</h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-body">{step.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </Container>
      </section>

      <CtaBand
        heading="Not sure which of these you need?"
        body="That is usually the right time to talk. Tell me what is happening and I will tell you where I would start."
        to="/contact"
        cta="Get in touch"
      />
    </div>
  );
}
